import { useState, useEffect, useCallback } from 'react';
import { getToken } from '../../utils/tokenManager';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 1 — Scoped performance hook.
 * Fetches per-asset-class / per-asset-type / per-investment time series.
 * Independent of the whole-portfolio chart data so the original flow stays untouched.
 */
export function useScopedPerformance() {
  // scope: 'portfolio' | 'asset_class' | 'asset_type' | 'investment'
  const [scope, setScope] = useState('portfolio');
  // value: 'all' for portfolio; otherwise the chosen class/type/investment_id
  const [scopeValue, setScopeValue] = useState('all');
  // currency: 'home' | 'native' (only meaningful for investment scope)
  const [currency, setCurrency] = useState('home');
  // period: passed straight to backend, matches existing whole-portfolio chart selector
  const [period, setPeriod] = useState('MONTHLY');
  // range: 'all' | '1m' | '3m' | '6m' | '1y' — applies to all scopes incl. portfolio
  const [range, setRange] = useState('all');

  const [scopes, setScopes] = useState({ asset_classes: [], asset_types: [], investments: [] });
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null); // { scope, filter, currency }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = useCallback(() => {
    const token = getToken();
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  }, []);

  // Load dropdown options once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/portfolio/performance/scopes`, { headers: headers(), cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setScopes(j);
      } catch (e) { logger.error('Failed to load performance scopes:', e); }
    })();
    return () => { cancelled = true; };
  }, [headers]);

  // Load the time series whenever scope/value/currency/period changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ period, range });
        if (scope === 'asset_class' && scopeValue && scopeValue !== 'all') params.set('asset_class', scopeValue);
        else if (scope === 'asset_type' && scopeValue && scopeValue !== 'all') params.set('asset_type', scopeValue);
        else if (scope === 'investment' && scopeValue && scopeValue !== 'all') {
          params.set('investment_id', scopeValue);
          params.set('currency', currency);
        }
        const r = await fetch(`${BACKEND_URL}/api/portfolio/performance?${params}`, { headers: headers(), cache: 'no-store' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) {
          setData(j.data || []);
          setMeta({ scope: j.scope, filter: j.filter, currency: j.currency });
        }
      } catch (e) {
        if (!cancelled) { setError(e); setData([]); }
        logger.error('Failed to load scoped performance:', e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, scopeValue, currency, period, range, headers]);

  return {
    scope, setScope, scopeValue, setScopeValue,
    currency, setCurrency, period, setPeriod, range, setRange,
    scopes, data, meta, isLoading, error,
  };
}
